import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ButtonPrimary } from "@/components/Buttons";
import { useAuth, formatApiError } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { CheckCircle2, Loader2, XCircle, KeyRound } from "lucide-react";

const inputClasses =
    "w-full bg-white border border-stone-200 focus:border-wine/60 rounded-sm px-4 py-3 text-sm text-slate-800 placeholder:text-slate-400 outline-none transition-colors duration-300";

export default function ResetPassword() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { openAuth } = useAuth();
    const token = searchParams.get("token");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (password !== confirm) {
            setError("Passwords do not match.");
            return;
        }
        setBusy(true);
        try {
            await api.post("/auth/reset-password", { token, new_password: password });
            setDone(true);
            toast.success("Password updated", { description: "Sign in with your new password." });
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center px-6 pt-20" data-testid="reset-password-page">
            <div className="max-w-md w-full bg-white border border-stone-200 rounded-sm px-8 py-14 text-center">
                {!token ? (
                    <>
                        <XCircle className="h-10 w-10 text-red-400 mx-auto" strokeWidth={1.5} />
                        <h1 className="mt-6 font-serif text-3xl font-light text-brand">Invalid reset link</h1>
                        <p className="mt-4 text-sm leading-relaxed text-mist">
                            This link is missing its token. Please request a new reset link from the sign-in window.
                        </p>
                    </>
                ) : done ? (
                    <>
                        <CheckCircle2 className="h-10 w-10 text-gold mx-auto" strokeWidth={1.5} />
                        <h1 className="mt-6 font-serif text-3xl font-light text-brand" data-testid="reset-success">
                            Password updated
                        </h1>
                        <p className="mt-4 text-sm leading-relaxed text-mist">
                            Your new password is active. Sign in to continue with your cart — your cart contents are
                            unchanged.
                        </p>
                        <div className="mt-8">
                            <ButtonPrimary
                                onClick={() => {
                                    navigate("/materials");
                                    openAuth("login");
                                }}
                                data-testid="reset-signin-btn"
                            >
                                Sign In
                            </ButtonPrimary>
                        </div>
                    </>
                ) : (
                    <>
                        <KeyRound className="h-9 w-9 text-gold mx-auto" strokeWidth={1.5} />
                        <h1 className="mt-6 font-serif text-3xl font-light text-brand">Choose a new password</h1>
                        <p className="mt-3 text-sm leading-relaxed text-mist">
                            Enter a new password for your Grayhaven Research account.
                        </p>
                        <form onSubmit={submit} className="mt-8 space-y-4 text-left" data-testid="reset-password-form">
                            <input
                                type="password"
                                required
                                minLength={8}
                                placeholder="New password (min 8 characters)"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                data-testid="reset-password-input"
                                className={inputClasses}
                                aria-label="New password"
                            />
                            <input
                                type="password"
                                required
                                minLength={8}
                                placeholder="Confirm new password"
                                value={confirm}
                                onChange={(e) => setConfirm(e.target.value)}
                                data-testid="reset-confirm-input"
                                className={inputClasses}
                                aria-label="Confirm new password"
                            />
                            {error && (
                                <p data-testid="reset-error" className="text-xs leading-relaxed text-red-700 bg-red-50 border border-red-200 rounded-sm px-4 py-3">
                                    {error}
                                </p>
                            )}
                            <button
                                type="submit"
                                disabled={busy}
                                data-testid="reset-submit-btn"
                                className="w-full inline-flex items-center justify-center gap-2 bg-wine text-white px-8 py-3 uppercase tracking-[0.2em] text-xs font-semibold hover:bg-wine-deep disabled:opacity-60 transition-colors duration-300"
                            >
                                {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                                Update Password
                            </button>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
}
